import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, Building2, Tag, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import NotesTimeline from '../components/NotesTimeline';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [emails, setEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    fetchContact();
  }, [id]);

  const fetchContact = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${id}`, {
        withCredentials: true,
      });
      setContact(response.data);
      // Fetch email history
      try {
        const em = await axios.get(`${API}/emails/by-contact/${id}`, { withCredentials: true });
        setEmails(em.data || []);
      } catch {}
      try {
        const t = await axios.get(`${API}/emails/templates`, { withCredentials: true });
        setTemplates(t.data || []);
      } catch {}
    } catch (error) {
      console.error('Error fetching contact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API}/contacts/${id}`, { withCredentials: true });
        navigate('/contacts');
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const statusColors = {
    lead: 'bg-blue-100 text-blue-800',
    customer: 'bg-green-100 text-green-800',
    partner: 'bg-purple-100 text-purple-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Contact not found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/contacts')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Contacts
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{contact.name}</h1>
            {contact.company && (
              <p className="text-lg text-gray-600 mt-1">{contact.company}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            {contact.email && (
              <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0176D3] hover:bg-[#0155A8] text-white">
                    <Mail className="w-4 h-4 mr-2" /> Send Email
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[560px]">
                  <DialogHeader>
                    <DialogTitle>Send Email to {contact.name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-600">To</label>
                      <Input value={contact.email} disabled />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Template</label>
                      <select
                        className="w-full border border-gray-300 rounded-md p-2"
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                      >
                        <option value="">— None —</option>
                        {templates.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Subject</label>
                      <Input
                        value={emailForm.subject}
                        onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                        placeholder="Subject"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Message</label>
                      <textarea
                        className="w-full border border-gray-300 rounded-md p-2 min-h-[160px]"
                        value={emailForm.body}
                        onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                        placeholder="Write your message..."
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsEmailOpen(false)}>Cancel</Button>
                      <Button
                        onClick={async () => {
                          if (!emailForm.subject.trim() && !selectedTemplate) return;
                          setEmailSending(true);
                          try {
                            const variables = {
                              'contact.name': contact.name || '',
                              'contact.email': contact.email || '',
                              'company.name': contact.company || ''
                            };
                            await axios.post(
                              `${API}/emails/send`,
                              {
                                contact_id: contact.id,
                                subject: emailForm.subject,
                                body_text: emailForm.body,
                                body_html: emailForm.body ? `<p>${emailForm.body.replace(/\n/g, '<br/>')}</p>` : undefined,
                                template_id: selectedTemplate || undefined,
                                variables
                              },
                              { withCredentials: true }
                            );
                            setIsEmailOpen(false);
                            setSelectedTemplate('');
                            setEmailForm({ subject: '', body: '' });
                            const em = await axios.get(`${API}/emails/by-contact/${id}`, { withCredentials: true });
                            setEmails(em.data || []);
                          } catch (e) {
                            console.error('Email send failed', e);
                          } finally {
                            setEmailSending(false);
                          }
                        }}
                        disabled={emailSending}
                        className="bg-[#0176D3] hover:bg-[#0155A8] text-white"
                      >
                        {emailSending ? 'Sending…' : 'Send'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Contact Info */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Badge className={statusColors[contact.status]}>
                  {contact.status}
                </Badge>
              </div>

              {contact.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <a href={`mailto:${contact.email}`} className="text-[#0176D3] hover:underline">
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}

              {contact.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <a href={`tel:${contact.phone}`} className="text-gray-900">
                      {contact.phone}
                    </a>
                  </div>
                </div>
              )}

              {contact.company && (
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Company</p>
                    <p className="text-gray-900">{contact.company}</p>
                  </div>
                </div>
              )}

              {contact.tags && contact.tags.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-5 h-5 text-gray-500" />
                    <p className="text-sm text-gray-600">Tags</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {contact.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {contact.notes && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Notes</p>
                  <p className="text-gray-900 text-sm">{contact.notes}</p>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Created: {new Date(contact.created_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Notes & Timeline */}
        <div className="lg:col-span-2">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Emails</CardTitle>
            </CardHeader>
            <CardContent>
                  {emails.length === 0 ? (
                <p className="text-sm text-gray-500">No emails yet.</p>
              ) : (
                <div className="space-y-3">
                  {emails.map((em) => (
                    <div key={em.id} className="border rounded-md p-3">
                      <div className="text-sm text-gray-600 flex justify-between">
                        <span>Subject: <span className="text-gray-900">{em.subject}</span></span>
                        <span>{new Date(em.sent_at).toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">To: {em.to} • Opens: {em.opens_count} • Clicks: {em.clicks_count}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <NotesTimeline
            entityType="contact"
            entityId={contact.id}
            entityName={contact.name}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactDetail;
